import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Chip,
  ChipProps,
  Divider,
  Fade,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';

import {
  currencyOptions,
  ICurrencyOption,
} from '../../../../../../../../../../constants';
import {
  adminRespondToLessonHistoryTutorPaymentConcern,
  adminUpdateLessonHistoryTutorPaymentAdjustments,
  ILessonHistory,
  ILessonHistoryTutorPaymentConcern,
  LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES,
  TAdminRespondToLessonHistoryTutorPaymentConcern,
  TAdminUpdateLessonHistoryTutorPaymentAdjustments,
  TLessonHistoryTutorPaymentConcernStatus,
} from '../../../../../../../../../../store/V2/LessonHistorySlice';
import { TPTextField } from '../../../../../../../../../../components/Inputs';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../../../../../components/V2/Buttons';

interface AdjustmentsTabProps {
  lessonHistory: ILessonHistory;
  refreshListEnabled?: boolean;
  refreshList?: () => void;
}

type TState = {
  tutorRate: string;
  tutorRateCurrencyOption: ICurrencyOption;
  duration: string;
  adminNote: string;
};

const initialState: TState = {
  tutorRate: '0',
  tutorRateCurrencyOption: {
    label: 'SGD',
    value: 'SGD',
  },
  duration: '0',
  adminNote: '',
};

const CustomContainer = ({ children }) => {
  return (
    <Card
      elevation={0}
      sx={{ backgroundColor: '#F8FAFC', borderRadius: '8px' }}
    >
      <CardContent style={{ padding: '16px' }}>{children}</CardContent>
    </Card>
  );
};

const ConcernStatusCard = ({
  concernData,
}: {
  concernData: ILessonHistoryTutorPaymentConcern;
}) => {
  let chipColor: ChipProps['color'] = 'warning';

  switch (concernData.status) {
    case LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES.PENDING:
      chipColor = 'warning';
      break;

    case LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES.RESOLVED:
      chipColor = 'success';
      break;

    case LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES.REJECTED:
      chipColor = 'error';
      break;

    default:
      chipColor = 'warning';
      break;
  }

  return (
    <Card
      elevation={0}
      sx={{ backgroundColor: '#F8FAFC', borderRadius: '8px' }}
    >
      <CardContent style={{ padding: '16px' }}>
        <Stack gap="8px">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography fontSize="16px" fontWeight={600} color="#1E293B">
              Tutor&apos;s Concern
            </Typography>
            <Chip
              color={chipColor}
              label={concernData.status.toLowerCase()}
              style={{
                textTransform: 'capitalize',
                padding: '4px 8px',
                height: 'unset',
                width: 'fit-content',
              }}
            />
          </Stack>
          <Typography fontSize="16px" fontWeight={500} color="#1E293B">
            {concernData.concern}
          </Typography>
          <Typography fontSize="12px" fontWeight={500} color="#1E293B">
            {moment(concernData.createdAt).format('DD MMM YYYY, hh:mm A')}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

const AdjustmentsTab: FunctionComponent<AdjustmentsTabProps> = ({
  lessonHistory,
  refreshListEnabled = false,
  refreshList,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const isSubmitDisabled =
    !state.tutorRate.trim().length ||
    !state.duration.trim().length ||
    Number(state.duration) < 0;

  useEffect(() => {
    if (lessonHistory) {
      setState((prevState) => {
        const newState = { ...prevState };

        if (typeof lessonHistory.tutorRate === 'number') {
          newState.tutorRate = lessonHistory.tutorRate.toString();
        }

        if (lessonHistory.tutorRateCurrency) {
          const matchingCurrency = currencyOptions.find(
            (item) => item.value === lessonHistory.tutorRateCurrency
          );

          if (matchingCurrency) {
            newState.tutorRateCurrencyOption = { ...matchingCurrency };
          }
        }

        if (lessonHistory && lessonHistory.duration) {
          newState.duration = lessonHistory.duration.toString();
        }

        return newState;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    if (state.duration && state.tutorRateCurrencyOption) {
      setLoading(true);
      const apiData: TAdminUpdateLessonHistoryTutorPaymentAdjustments = {
        lessonHistoryId: lessonHistory._id,
        tutorRate: parseFloat(state.tutorRate),
        tutorRateCurrency: state.tutorRateCurrencyOption.value,
        duration: parseFloat(state.duration),
      };

      await dispatch(adminUpdateLessonHistoryTutorPaymentAdjustments(apiData));

      if (refreshListEnabled && refreshList) {
        refreshList();
      }

      setLoading(false);
    }
  };

  const handleConcernRespond = async (
    response: TLessonHistoryTutorPaymentConcernStatus
  ) => {
    setLoading(true);

    const apiData: TAdminRespondToLessonHistoryTutorPaymentConcern = {
      lessonHistoryId: lessonHistory._id,
      status: response,
      adminNote: state.adminNote,
    };

    await dispatch(adminRespondToLessonHistoryTutorPaymentConcern(apiData));

    setLoading(false);
  };

  return (
    <Fade in>
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomContainer>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize="16px" fontWeight={500} color="#647488">
                  Tutor Rate
                </Typography>
                <Stack direction="row" alignItems="center" gap="10px">
                  <div style={{ minWidth: '130px', maxWidth: '130px' }}>
                    <TPTextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Rate"
                      value={state.tutorRate}
                      InputProps={{ sx: { backgroundColor: '#fff' } }}
                      onChange={(event) =>
                        updateStateAttributes({ tutorRate: event.target.value })
                      }
                    />
                  </div>
                  <div style={{ minWidth: '130px', maxWidth: '130px' }}>
                    <Autocomplete
                      fullWidth
                      disableClearable
                      size="small"
                      value={state.tutorRateCurrencyOption}
                      options={currencyOptions}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      onChange={(event, value) =>
                        updateStateAttributes({
                          tutorRateCurrencyOption: value,
                        })
                      }
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          className="mui-text-field-label-hidden"
                          placeholder="Currency"
                          InputProps={{
                            ...params.InputProps,
                            sx: { backgroundColor: '#fff' },
                          }}
                        />
                      )}
                    />
                  </div>
                </Stack>
              </Stack>
            </CustomContainer>
          </Grid>
          <Grid item xs={12}>
            <CustomContainer>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize="16px" fontWeight={500} color="#647488">
                  Duration (mins)
                </Typography>
                <Stack direction="row" alignItems="center" gap="10px">
                  <div style={{ minWidth: '130px', maxWidth: '130px' }}>
                    <TPTextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Duration"
                      value={state.duration}
                      InputProps={{ sx: { backgroundColor: '#fff' } }}
                      onChange={(event) =>
                        updateStateAttributes({ duration: event.target.value })
                      }
                    />
                  </div>
                </Stack>
              </Stack>
            </CustomContainer>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end">
              <DarkLoadingButton
                variant="contained"
                color="inherit"
                loading={isLoading}
                disabled={isSubmitDisabled}
                onClick={handleSubmit}
                sx={{ ...defaultButtonStyles }}
              >
                Save
              </DarkLoadingButton>
            </Stack>
          </Grid>
          {lessonHistory.tutorPaymentConcern ? (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <ConcernStatusCard
                  concernData={lessonHistory.tutorPaymentConcern}
                />
              </Grid>
              {lessonHistory.tutorPaymentConcern.status ===
              LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES.PENDING ? (
                <>
                  <Grid item xs={12}>
                    <CustomContainer>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography fontSize="16px" fontWeight={600}>
                            Admin Remarks *
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={state.adminNote}
                            name="adminNote"
                            placeholder="Type here..."
                            InputProps={{ sx: { backgroundColor: '#fff' } }}
                            onChange={(event) =>
                              updateStateAttributes({
                                adminNote: event.target.value,
                              })
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Stack
                            direction="row"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap="10px"
                          >
                            <Button
                              disableElevation
                              disabled={isLoading}
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() =>
                                handleConcernRespond(
                                  LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES.REJECTED
                                )
                              }
                            >
                              Reject
                            </Button>
                            <Button
                              disableElevation
                              disabled={isLoading}
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={() =>
                                handleConcernRespond(
                                  LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES.RESOLVED
                                )
                              }
                            >
                              Resolve
                            </Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CustomContainer>
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <CustomContainer>
                    <Stack gap="8px">
                      <Typography
                        fontSize="16px"
                        fontWeight={600}
                        color="#1E293B"
                      >
                        Admin Remarks
                      </Typography>
                      <Typography
                        fontSize="16px"
                        fontWeight={500}
                        color="#1E293B"
                      >
                        {lessonHistory.tutorPaymentConcern.adminNote || '-'}
                      </Typography>
                      {lessonHistory.tutorPaymentConcern.respondedAt ? (
                        <Typography
                          fontSize="12px"
                          fontWeight={500}
                          color="#1E293B"
                        >
                          {moment(
                            lessonHistory.tutorPaymentConcern.respondedAt
                          ).format('DD MMM YYYY, hh:mm A')}
                        </Typography>
                      ) : null}
                    </Stack>
                  </CustomContainer>
                </Grid>
              )}
            </>
          ) : null}
        </Grid>
      </div>
    </Fade>
  );
};

export default AdjustmentsTab;
